import { create } from "zustand";

import { authService } from "./services/authService";
import { authCookies } from "@/lib/cookies";
import { clearDraft as clearObsDraft } from "@/features/obs/lib/draft-store";
import type { User } from "@/types/user";
import type {
  LoginRequest,
  LoginResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from "@/types/auth";
import { ACCESS_TOKEN_REFRESHED_EVENT } from "@/constants/auth";
import { Routes } from "@/constants/enums";
import {
  ClientHttpError,
  extractTokensFromWrappedAuthBody,
} from "@/lib/http-client";

interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  initiateLogin: (credentials: LoginRequest) => Promise<LoginResponse>;
  resendOtp: (email: string) => Promise<{ message?: string }>;
  verifyOtp: (payload: VerifyOtpRequest) => Promise<VerifyOtpResponse>;
  ssoExchange: (code: string) => Promise<VerifyOtpResponse>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  refreshTokens: () => Promise<void>;
  clearAuth: () => void;
}

// A persisted session must be validated on boot whenever ANY auth artifact
// survives in cookies — a token to verify, or only a stale `user_data` cookie
// whose access token has already expired/been dropped. We call /me at startup;
// the http client transparently refreshes the JWT via the Strapi HttpOnly
// refreshToken cookie on a 401, and clears cookies + redirects on failure. This
// prevents a leftover `user_data` cookie from keeping the app "logged in" after
// the access token is gone.
const sessionNeedsValidation = (
  accessToken: string | null,
  user: User | null,
): boolean => {
  return !!accessToken || !!user;
};

// Initialize authentication state from cookies. Only a real access token counts
// as an authenticated session; a lone `user_data` cookie does not. When any
// artifact exists we start in the loading state until boot validation resolves.
const initializeAuthState = () => {
  const { accessToken } = authCookies.getTokens();
  const user = authCookies.getUser();

  return {
    user,
    accessToken,
    isLoading: sessionNeedsValidation(accessToken, user),
    isAuthenticated: !!accessToken,
  };
};

export const useAuthStore = create<AuthState>()((set, get) => ({
  // Initial state
  ...initializeAuthState(),
  // Actions
  setUser: (user) =>
    set(() => ({
      user,
      isAuthenticated: !!user,
    })),

  setLoading: (loading) =>
    set(() => ({
      isLoading: loading,
    })),

  initiateLogin: async (credentials) => {
    return authService.login(credentials);
  },

  resendOtp: async (email) => {
    return authService.resendOtp(email);
  },

  verifyOtp: async (payload) => {
    const response: VerifyOtpResponse = await authService.verifyOtp(payload);

    let userData = response.data.user;

    if (!userData && response.data.accessToken) {
      try {
        userData = await authService.getCurrentUser();
      } catch (userError) {
        console.error("Failed to fetch user data after login:", userError);
      }
    }

    const { accessToken: extractedAccess } =
      extractTokensFromWrappedAuthBody(response);
    const access = response.data.accessToken ?? extractedAccess ?? null;

    set(() => ({
      user: userData,
      accessToken: access,
      isAuthenticated: true,
    }));

    if (access) {
      authCookies.setAccessToken(access);
      scheduleProactiveRefresh(access);
    }
    if (userData) {
      authCookies.setUser(userData);
    }

    return response;
  },

  ssoExchange: async (code) => {
    const response: VerifyOtpResponse = await authService.ssoExchange(code);

    let userData = response.data.user;
    if (!userData && response.data.accessToken) {
      try {
        userData = await authService.getCurrentUser();
      } catch (userError) {
        console.error("Failed to fetch user data after SSO:", userError);
      }
    }

    const { accessToken: extractedAccess } =
      extractTokensFromWrappedAuthBody(response);
    const access = response.data.accessToken ?? extractedAccess ?? null;

    set(() => ({
      user: userData,
      accessToken: access,
      isAuthenticated: true,
    }));

    if (access) {
      authCookies.setAccessToken(access);
      scheduleProactiveRefresh(access);
    }
    if (userData) authCookies.setUser(userData);

    return response;
  },

  logout: async () => {
    cancelProactiveRefresh();
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      // Clear state regardless of API success
      set(() => ({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
      }));

      // Clear cookies
      authCookies.clearAll();

      // Redirect to login
      window.location.href = Routes.ROOT;
    }
  },

  refreshUser: async () => {
    const accessToken =
      get().accessToken ?? authCookies.getAccessToken() ?? null;
    if (!accessToken) return;

    set(() => ({ isLoading: true }));

    try {
      const user = await authService.getCurrentUser();
      set(() => ({
        user,
        accessToken: authCookies.getAccessToken(),
        isAuthenticated: true,
        isLoading: false,
      }));

      // Update user data in cookies
      authCookies.setUser(user);
    } catch (error) {
      console.error("Failed to refresh user:", error);
      // If refresh fails, clear auth state
      get().clearAuth();
    }
  },

  refreshTokens: async () => {
    try {
      const response = await authService.refreshToken();
      const access = response.data?.accessToken ?? null;

      if (access) {
        authCookies.setAccessToken(access);
        set(() => ({ accessToken: access }));
        scheduleProactiveRefresh(access);
      }
    } catch (error) {
      console.error("Failed to refresh tokens:", error);
      get().clearAuth();
      throw error;
    }
  },

  clearAuth: () => {
    cancelProactiveRefresh();
    set(() => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
    }));

    // Clear cookies
    authCookies.clearAll();

    // 🔴 An unfinished OBS report keeps a ministry budget workbook in browser
    // storage so it survives a reload. Signing out has to take it with it —
    // otherwise the next person at this machine inherits the file and every
    // figure in it. Fire-and-forget: a storage failure must never block a logout.
    void clearObsDraft();
  },
}));

// ---------------------------------------------------------------------------
// Proactive token refresh
// ---------------------------------------------------------------------------
// The access token has a short lifetime (default 15 min). Without a proactive
// refresh the first API call after expiry triggers a 401 → silent-refresh →
// retry cycle — one extra round trip that is invisible most of the time but
// can fail under a brief network outage and produce a spurious logout.
//
// Instead we schedule a refresh ~1 minute before the token's `exp` claim, so
// the new token is in place before any request needs it.
// ---------------------------------------------------------------------------

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function cancelProactiveRefresh() {
  if (proactiveRefreshTimer !== null) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

function scheduleProactiveRefresh(accessToken: string) {
  cancelProactiveRefresh();

  const exp = decodeJwtExp(accessToken);
  if (!exp) return;

  // Fire 60 seconds before expiry (clamped to 0 so we don't schedule into the past).
  const msUntilRefresh = Math.max((exp - 60) * 1000 - Date.now(), 0);

  proactiveRefreshTimer = setTimeout(async () => {
    proactiveRefreshTimer = null;
    try {
      // authService.refreshToken() goes through the normal http client which
      // will update the cookie and fire ACCESS_TOKEN_REFRESHED_EVENT, which
      // in turn calls scheduleProactiveRefresh again for the next cycle.
      const response = await authService.refreshToken();
      const newAccess = response.data?.accessToken ?? null;
      if (newAccess) {
        authCookies.setAccessToken(newAccess);
        useAuthStore.setState({ accessToken: newAccess });
        scheduleProactiveRefresh(newAccess);
      }
    } catch {
      // Proactive refresh failed silently — the http client will handle the
      // next 401 automatically; only a hard auth failure will redirect to login.
    }
  }, msUntilRefresh);
}

if (typeof window !== "undefined") {
  window.addEventListener(ACCESS_TOKEN_REFRESHED_EVENT, (event: Event) => {
    const { detail } = event as CustomEvent<{ accessToken: string }>;
    const token = detail?.accessToken;
    if (!token) return;

    const stateUser = useAuthStore.getState().user;
    const cookieUser = authCookies.getUser();
    let user = stateUser ?? cookieUser;

    // Refresh succeeded — keep session. Never set isAuthenticated false here just because
    // Zustand `user` was null (race with hydration or cookie-only user).
    useAuthStore.setState({
      accessToken: token,
      isAuthenticated: true,
      isLoading: !user,
      ...(user && !stateUser ? { user } : {}),
    });

    // Schedule proactive refresh for the new token.
    scheduleProactiveRefresh(token);

    if (user) return;

    void (async () => {
      try {
        user = await authService.getCurrentUser();
        authCookies.setUser(user);
        useAuthStore.setState({
          user,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        useAuthStore.setState({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
        authCookies.clearAll();
      }
    })();
  });
}

/** 401/403 from our API is a verdict on the session; anything else (429, 5xx, offline) is not. */
function isDefinitiveSessionRejection(error: unknown): boolean {
  return (
    error instanceof ClientHttpError &&
    (error.response.status === 401 || error.response.status === 403)
  );
}

let initialSessionHydrationStarted = false;
function runInitialSessionHydration() {
  if (typeof window === "undefined" || initialSessionHydrationStarted) return;
  const { accessToken } = authCookies.getTokens();
  const user = authCookies.getUser();
  const needsValidation = sessionNeedsValidation(accessToken, user);
  const shouldTrySilentRefresh = !accessToken;

  if (!needsValidation && !shouldTrySilentRefresh) return;
  initialSessionHydrationStarted = true;

  if (needsValidation) {
    useAuthStore.setState({ isLoading: true });
  }

  void (async () => {
    try {
      if (!accessToken && shouldTrySilentRefresh) {
        const refreshed = await authService.refreshToken();
        const newAccess = refreshed.data?.accessToken ?? null;
        if (newAccess) {
          authCookies.setAccessToken(newAccess);
          useAuthStore.setState({ accessToken: newAccess });
        }
      }

      const userData = await authService.getCurrentUser();
      authCookies.setUser(userData);
      const hydratedToken = authCookies.getAccessToken();
      useAuthStore.setState({
        user: userData,
        accessToken: hydratedToken,
        isAuthenticated: true,
        isLoading: false,
      });
      // Start proactive refresh cycle for the current access token.
      if (hydratedToken) scheduleProactiveRefresh(hydratedToken);
    } catch (error) {
      // Only the server saying the session is invalid (401/403) ends it. A 429, a 5xx or a
      // network failure says nothing about the session — the backend's auth rate limit once
      // signed users out on an ordinary refresh this way (2026-09-26). Keep what the cookies
      // hold; the next API call re-validates through the http client's refresh-on-401.
      if (!isDefinitiveSessionRejection(error)) {
        const keptToken = authCookies.getAccessToken();
        useAuthStore.setState({
          user: authCookies.getUser(),
          accessToken: keptToken,
          isAuthenticated: !!keptToken,
          isLoading: false,
        });
        if (keptToken) scheduleProactiveRefresh(keptToken);
        return;
      }
      if (needsValidation || shouldTrySilentRefresh) {
        authCookies.clearAll();
        useAuthStore.setState({
          user: null,
          accessToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      }
    }
  })();
}

runInitialSessionHydration();

// Selectors for specific state slices
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () =>
  useAuthStore((state) => state.isAuthenticated);
export const useIsLoading = () => useAuthStore((state) => state.isLoading);

// Token selectors
export const useAccessToken = () => useAuthStore((state) => state.accessToken);
export const useTokens = () =>
  useAuthStore((state) => ({ accessToken: state.accessToken }));

// Individual action selectors - more stable than returning an object
export const useInitiateLogin = () => useAuthStore((state) => state.initiateLogin);
export const useResendOtp = () => useAuthStore((state) => state.resendOtp);
export const useVerifyOtp = () => useAuthStore((state) => state.verifyOtp);
export const useLogout = () => useAuthStore((state) => state.logout);
export const useRefreshUser = () => useAuthStore((state) => state.refreshUser);
export const useClearAuth = () => useAuthStore((state) => state.clearAuth);
